/**
 * Servicio base para proveedores de IA
 * Usa resumen de usuario y formato compacto para reducir tokens en el prompt.
 */

import {
  getWatchedCompactLines,
  getLowRatedCompactLines,
  getRandomLikedCompactLines,
  getRandomDislikedCompactLines,
  getRandomWatchedCompactLines,
  buildUserSummaryText,
  buildCrossContentSummary,
  formatItemCompact,
  formatItemCompactWithRating,
  getExistingRecommendationsCompactLines,
  MAX_WATCHED_IN_PROMPT,
  MAX_LOW_RATED_IN_PROMPT,
  MAX_LIKED_IN_PROMPT,
  MAX_DISLIKED_IN_PROMPT,
  MAX_EXISTING_RECOMMENDATIONS_IN_PROMPT
} from '../utils/userSummaryUtils';
import { promptTranslations } from '../utils/promptTranslations';
import { log as debugLog } from '../utils/logger';

class AIProviderService {
  constructor() {
    this.provider = process.env.AI_PROVIDER || 'openwebui';
    this.temperature = parseFloat(process.env.AI_TEMPERATURE) || 0.1;
    this.maxTokens = parseInt(process.env.AI_MAX_TOKENS) || 2000;
    this.loggingEnabled = true;
  }

  /**
   * Log detallado de operaciones
   * @param {string} operation - Operación realizada
   * @param {Object} data - Datos relacionados
   * @param {string} level - Nivel de log (info, warn, error)
   */
  log(operation, data = {}, level = 'info') {
    if (!this.loggingEnabled) return;
    const timestamp = new Date().toISOString();
    debugLog(`[${timestamp}] AI_PROVIDER ${level.toUpperCase()}: ${operation}`, data);
  }

  /**
   * Verifica si el proveedor está disponible
   * @returns {Promise<boolean>} True si está disponible
   */
  async isAvailable() {
    throw new Error('isAvailable method must be implemented by provider');
  }

  /**
   * Genera recomendaciones
   * @param {Object} userData - Datos del usuario
   * @param {string} contentType - Tipo de contenido
   * @param {number} count - Número de recomendaciones
   * @returns {Promise<Array>} Recomendaciones generadas
   */
  async generateRecommendations(userData, contentType = 'movie', count = 20) {
    throw new Error('generateRecommendations method must be implemented by provider');
  }

  /**
   * Calcula calificación de IA para contenido específico
   * @param {Object} content - Contenido a analizar
   * @param {Object} userPreferences - Preferencias del usuario
   * @returns {Promise<number>} Calificación de IA
   */
  async calculateAIRating(content, userPreferences) {
    throw new Error('calculateAIRating method must be implemented by provider');
  }

  /**
   * Construye un prompt unificado para cualquier proveedor de IA
   * @param {Object} userData - Datos del usuario
   * @param {string} contentType - Tipo de contenido (movie, series, music)
   * @param {number} count - Número de recomendaciones
   * @param {string} language - Idioma ('es' o 'en')
   * @returns {string} Prompt optimizado
   */
  buildUnifiedPrompt(userData, contentType = 'movie', count = 20, language = 'es') {
    const t = promptTranslations[language] || promptTranslations.es;
    
    const contentTypeMap = t.contentType;
    const contentLabel = contentTypeMap[contentType] || (language === 'en' ? 'content' : 'contenido');
    const watchedContent = userData.watchedContent || {};

    // CONTENIDO YA VISTO: muestra ALEATORIA de 50 ítems para que cada petición vea contexto distinto
    const watchedLines = getRandomWatchedCompactLines(watchedContent, contentType, MAX_WATCHED_IN_PROMPT);
    const watchedBlock = watchedLines.length
      ? watchedLines.join('\n')
      : t.watchedBlock.none;

    // Resumen de gustos (géneros, décadas, preferencias)
    const summaryText = buildUserSummaryText(userData, contentType);

    // Le gustaron (rating >= 6.5): muestra ALEATORIA con puntuación para contexto dinámico
    const likedLines = getRandomLikedCompactLines(watchedContent, contentType, MAX_LIKED_IN_PROMPT);
    const likedBlock = likedLines.length
      ? likedLines.join('\n')
      : t.liked.none;

    // No le gustaron (rating < 6.5): muestra ALEATORIA con puntuación
    const lowRatedLines = getRandomDislikedCompactLines(watchedContent, contentType, MAX_DISLIKED_IN_PROMPT);
    const lowRatedBlock = lowRatedLines.length
      ? lowRatedLines.join('\n')
      : '';

    // Contenido recién valorado (si acaba de calificar algo): contexto para esta petición
    const recentlyRated = userData.recentlyRated;
    const recentlyRatedLine = recentlyRated
      ? formatItemCompactWithRating(
          {
            title: recentlyRated.title,
            year: recentlyRated.year,
            genre: recentlyRated.genre,
            artist: recentlyRated.artist,
            ratings: { user_rating: recentlyRated.ratings?.user_rating ?? recentlyRated.user_rating },
            user_rating: recentlyRated.ratings?.user_rating ?? recentlyRated.user_rating
          },
          contentType
        )
      : '';

    const favoriteActorsArtists = this.getFavoriteActorsArtists(userData, contentType);

    // Recomendaciones existentes: para evitar duplicados
    const existingRecommendations = userData.existingRecommendations || [];
    const existingRecommendationsCount = existingRecommendations.length;
    
    // Crear lista de solo títulos para comparación rápida (más fácil para el modelo)
    const existingForPrompt = existingRecommendations.slice(0, MAX_EXISTING_RECOMMENDATIONS_IN_PROMPT);
    const existingTitlesOnly = existingForPrompt
      .map((r) => (r.title || '').toLowerCase().trim().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
    
    const existingRecommendationsLines = getExistingRecommendationsCompactLines(
      existingForPrompt,
      contentType,
      MAX_EXISTING_RECOMMENDATIONS_IN_PROMPT
    );
    const existingRecommendationsBlock = existingRecommendationsLines.length
      ? existingRecommendationsLines.join('\n')
      : '';
    
    // Crear bloque de solo títulos para comparación rápida
    const existingTitlesBlock = existingTitlesOnly.length > 0
      ? `\nTÍTULOS DE RECOMENDACIONES EXISTENTES (NO repetir estos títulos):\n${existingTitlesOnly.join(', ')}\n`
      : '';

    // RAG: contexto recuperado por similitud (formato compacto con 1 género)
    const retrievedLines = (userData.retrievedContext || []).map((item) =>
      formatItemCompact(
        { title: item.title, year: item.year, genre: item.genre, artist: item.artist },
        contentType,
        false // Mantener género para contexto RAG
      )
    );
    const retrievedBlock = retrievedLines.length
      ? retrievedLines.join('\n')
      : '';

    const useCrossContent = userData.useCrossContentContext === true;
    const crossContentBlock = useCrossContent
      ? buildCrossContentSummary(watchedContent, contentType)
      : '';

    // Obtener fecha/año actual para que el modelo sepa qué películas ya fueron estrenadas
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 1-12
    const currentDateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;

    const hasExisting = existingRecommendationsCount > 0;
    const criticalSection = hasExisting
      ? t.criticalSection.withExisting(existingRecommendationsCount, contentLabel, count)
      : t.criticalSection.noExisting(count, contentLabel);

    const existingBlockInPrompt = hasExisting && existingRecommendationsBlock
      ? `\n🚫 ${t.existingRecommendations.title} - ${t.existingRecommendations.warning} (${Math.min(existingRecommendations.length, MAX_EXISTING_RECOMMENDATIONS_IN_PROMPT)} ${contentLabel} ${language === 'en' ? 'already recommended' : 'ya recomendadas'}) 🚫:\n${existingRecommendationsBlock}\n\n⚠️ ${t.existingRecommendations.critical}\n`
      : '';

    const rule3NoRepetir = hasExisting
      ? t.rules.rule3(contentLabel, existingRecommendationsCount) + '\n'
      : '';

    const rememberLine = hasExisting
      ? '\n' + t.rules.remember(contentLabel)
      : '';

    const expertText = language === 'en' 
      ? `You are an expert in ${contentLabel} recommendations.`
      : `Eres un experto en recomendaciones de ${contentLabel}.`;
    const generateText = language === 'en'
      ? `Generate ${count} REAL AND EXISTING ${contentLabel} recommendations in JSON format.`
      : `Genera ${count} recomendaciones de ${contentLabel} REALES Y EXISTENTES en formato JSON.`;

    const prompt = `${expertText} ${generateText}

${criticalSection}

${t.dateReference(currentDateStr, currentYear)}

${useCrossContent ? t.crossContent(contentLabel, crossContentBlock) : ''}

${language === 'en' ? 'USER TASTE SUMMARY' : 'RESUMEN DE GUSTOS DEL USUARIO'}: ${summaryText}
${retrievedBlock ? t.retrievedContext(retrievedBlock) : ''}
${recentlyRatedLine ? t.recentlyRated(recentlyRatedLine) : ''}

${t.watchedBlock.label} – ${language === 'en' ? 'maximum' : 'máximo'} ${MAX_WATCHED_IN_PROMPT} ${language === 'en' ? 'items' : 'ítems'}:
${watchedBlock}
${hasExisting ? existingTitlesBlock : ''}
${existingBlockInPrompt}
${contentLabel.toUpperCase()} ${t.liked.label} (rating ≥ 6.5) – ${language === 'en' ? 'random sample with rating' : 'muestra aleatoria con puntuación'}:
${likedBlock}
${lowRatedBlock ? `\n${contentLabel.toUpperCase()} ${t.disliked.label} (rating < 6.5) – ${t.disliked.avoid}:\n${lowRatedBlock}\n` : ''}

${contentType === 'music' ? t.favorites.artists : t.favorites.actors}:
${favoriteActorsArtists.join(', ') || t.favorites.none}

${language === 'en' ? 'Respond ONLY with valid JSON' : 'Responde SOLO con JSON válido'}:
${contentType === 'music'
  ? `{
  "recommendations": [
    {
      "title": "${t.jsonExample.music.title}",
      "year": 2020,
      "genre": ["${language === 'en' ? 'Genre1' : 'Género1'}", "${language === 'en' ? 'Genre2' : 'Género2'}"],
      "artist": "${t.jsonExample.music.artist}",
      "external_rating": 8.5,
      "ai_rating": 8.8,
      "reason": "${language === 'en' ? 'Reason based on preferences and similar content' : 'Razón basada en preferencias y contenido similar'}"
    }
  ]
}`
  : `{
  "recommendations": [
    {
      "title": "${t.jsonExample.movie.title}",
      "year": 2020,
      "genre": ["${language === 'en' ? 'Genre1' : 'Género1'}", "${language === 'en' ? 'Genre2' : 'Género2'}"],
      "director": "${t.jsonExample.movie.director}",
      "actors": ["${t.jsonExample.movie.actors[0]}", "${t.jsonExample.movie.actors[1]}"],
      "external_rating": 8.5,
      "ai_rating": 8.8,
      "reason": "${language === 'en' ? 'Reason based on preferences and similar content' : 'Razón basada en preferencias y contenido similar'}"
    }
  ]
}`}

${t.rules.title}:

${t.rules.rule1(contentLabel, contentType === 'music')}

${t.rules.rule2}
${rule3NoRepetir}${language === 'en' ? '4' : '4'}. ${language === 'en' 
  ? 'DO NOT repeat from "THAT THEY LIKED" or "ALREADY WATCHED CONTENT" - those are already in history'
  : 'NO repitas de "QUE LE GUSTARON" ni de "CONTENIDO YA VISTO" - esas ya están en el historial'}

${t.rules.rule5(contentLabel)}

${t.rules.rule6(contentLabel)}

${t.rules.rule7(contentLabel)}

${t.rules.rule8(contentType)}

${t.rules.rule9}

${t.rules.rule10(count)}
${rememberLine}`;

    debugLog('[PROMPT ENVIADO A LA IA]\n' + prompt);
    return prompt;
  }

  /**
   * Obtiene las mejores calificadas de un tipo de contenido específico
   * @param {Object} userData - Datos del usuario
   * @param {string} contentType - Tipo de contenido
   * @param {number} limit - Número máximo de elementos
   * @returns {Array} Array de contenido mejor calificado
   */
  getTopRatedContent(userData, contentType, limit = 10) {
    const watchedContent = userData.watchedContent || {};
    let content = [];
    
    switch (contentType) {
      case 'movie':
        content = watchedContent.movies || [];
        break;
      case 'series':
        content = watchedContent.series || [];
        break;
      case 'music':
        content = watchedContent.music || [];
        break;
      default:
        return [];
    }

    // Ordenar por calificación promedio (descendente) y tomar los mejores
    return content
      .filter(item => item.average_rating || item.external_rating || item.ai_rating)
      .sort((a, b) => {
        const ratingA = a.average_rating || a.external_rating || a.ai_rating || 0;
        const ratingB = b.average_rating || b.external_rating || b.ai_rating || 0;
        return ratingB - ratingA;
      })
      .slice(0, limit)
      .map(item => ({
        title: item.title,
        year: item.year,
        genre: Array.isArray(item.genre) ? item.genre.slice(0, 2) : [item.genre],
        rating: item.average_rating || item.external_rating || item.ai_rating,
        ...(contentType === 'music' ? { artist: item.artist } : { 
          director: item.director,
          actors: Array.isArray(item.actors) ? item.actors.slice(0, 3) : [item.actors]
        })
      }));
  }

  /**
   * Obtiene actores o artistas favoritos del contenido visto
   * @param {Object} userData - Datos del usuario
   * @param {string} contentType - Tipo de contenido
   * @returns {Array} Array de actores/artistas favoritos
   */
  getFavoriteActorsArtists(userData, contentType) {
    const watchedContent = userData.watchedContent || {};
    let content = [];
    
    switch (contentType) {
      case 'movie':
        content = watchedContent.movies || [];
        break;
      case 'series':
        content = watchedContent.series || [];
        break;
      case 'music':
        content = watchedContent.music || [];
        break;
      default:
        return [];
    }

    if (contentType === 'music') {
      // Para música, obtener artistas únicos
      const artists = new Set();
      content.forEach(item => {
        if (item.artist) {
          artists.add(item.artist);
        }
      });
      return Array.from(artists).slice(0, 10);
    } else {
      // Para películas y series, obtener actores únicos
      const actors = new Set();
      content.forEach(item => {
        if (Array.isArray(item.actors)) {
          item.actors.forEach(actor => {
            if (actor && actor.trim()) {
              actors.add(actor.trim());
            }
          });
        } else if (item.actors && item.actors.trim()) {
          actors.add(item.actors.trim());
        }
      });
      return Array.from(actors).slice(0, 15);
    }
  }

  /**
   * Obtiene títulos ya vistos para un tipo de contenido específico
   * @param {Object} userData - Datos del usuario
   * @param {string} contentType - Tipo de contenido
   * @returns {Array} Array de títulos ya vistos
   */
  getWatchedTitlesForType(userData, contentType) {
    const watchedContent = userData.watchedContent || {};
    
    switch (contentType) {
      case 'movie':
        return (watchedContent.movies || []).map(item => item.title);
      case 'series':
        return (watchedContent.series || []).map(item => item.title);
      case 'music':
        return (watchedContent.music || []).map(item => item.title);
      default:
        return [];
    }
  }

  /**
   * Construye el prompt para recomendaciones
   * @param {Object} userData - Datos del usuario
   * @param {string} contentType - Tipo de contenido
   * @param {number} count - Número de recomendaciones
   * @returns {string} Prompt construido
   */
  buildRecommendationPrompt(userData, contentType = 'movie', count = 20) {
    const contentTypeMap = {
      movie: 'películas',
      series: 'series',
      music: 'música'
    };

    const contentLabel = contentTypeMap[contentType] || 'contenido';

    return `Basándote en las siguientes preferencias del usuario, genera ${count} recomendaciones de ${contentLabel} en formato JSON:

PELÍCULAS VISTAS:
${JSON.stringify(userData.watchedContent?.movies || [], null, 2)}

SERIES VISTAS:
${JSON.stringify(userData.watchedContent?.series || [], null, 2)}

MÚSICA ESCUCHADA:
${JSON.stringify(userData.watchedContent?.music || [], null, 2)}

PREFERENCIAS DEL USUARIO:
${JSON.stringify(userData.preferences || {}, null, 2)}

Responde SOLO con un JSON válido en este formato:
${contentType === 'music'
  ? `{
  "recommendations": [
    {
      "title": "Título de canción",
      "year": 2020,
      "genre": ["Género1", "Género2"],
      "artist": "Artista o banda",
      "external_rating": 8.5,
      "reason": "Razón de la recomendación basada en las preferencias del usuario"
    }
  ]
}`
  : `{
  "recommendations": [
    {
      "title": "Título del ${contentLabel.slice(0, -1)}",
      "year": 2020,
      "genre": ["Género1", "Género2"],
      "director": "Nombre del director",
      "actors": ["Actor1", "Actor2"],
      "external_rating": 8.5,
      "reason": "Razón de la recomendación basada en las preferencias del usuario"
    }
  ]
}`}

IMPORTANTE: 
- Responde ÚNICAMENTE con JSON válido
- No incluyas texto adicional
- Asegúrate de que el JSON sea válido
- Las recomendaciones deben ser relevantes a las preferencias del usuario`;
  }
}

export default AIProviderService;
